import { Suspense, lazy } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { Route, Router, Switch } from 'react-router-dom';

import isValidProp from '@emotion/is-prop-valid';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { NatterThemeProvider } from 'common/hocs';
import { VISUAL_REFRESH, applyVisualRefreshClass, logEnabledFeatures } from 'common/utils';
import { MotionConfig } from 'framer-motion';
import { AuthProvider } from 'modules/authorization';
import { KeycloakProvider } from 'modules/keycloak/context';
import { createRoot } from 'react-dom/client';
import { reduxHistory } from 'reduxHistory';
import { store } from 'store';

import './components/styles/design-system.css';
import { initDatadogLogs } from './datadog-logs';
import { initDatadogRum } from './datadog-rum';
import i18n from './i18n';
import './index.css';
import { initBrowserMsw } from './mocks/initBrowserMsw';
import { PreApp } from './preApp';
import { initSentry } from './sentry';

const DeviceTestPage = import.meta.env.DEV
  ? lazy(() => import('pages/DeviceTest/DeviceTestPage').then((m) => ({ default: m.DeviceTestPage })))
  : null;

initSentry();
initDatadogRum();
initDatadogLogs();
initBrowserMsw();
logEnabledFeatures();

/** Hand the resolved flag to the two `font-family` declarations CSS owns (HNS-260). */
applyVisualRefreshClass(VISUAL_REFRESH);

const container = document.getElementById('root')!;
const root = createRoot(container);

root.render(
  <I18nextProvider i18n={i18n}>
    <Router history={reduxHistory}>
      <StyledEngineProvider injectFirst>
        <MotionConfig reducedMotion="user" isValidProp={isValidProp}>
          <NatterThemeProvider>
            <CssBaseline />
            <Provider store={store}>
              <Switch>
                {DeviceTestPage && (
                  <Route exact path="/dev/device-test">
                    <Suspense fallback={null}>
                      <DeviceTestPage />
                    </Suspense>
                  </Route>
                )}
                <Route>
                  <AuthProvider>
                    <KeycloakProvider>
                      <PreApp />
                    </KeycloakProvider>
                  </AuthProvider>
                </Route>
              </Switch>
            </Provider>
          </NatterThemeProvider>
        </MotionConfig>
      </StyledEngineProvider>
    </Router>
  </I18nextProvider>
);
